import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useApi } from "@/lib/api";
import { TaskDetail } from "@/pages/AdminQueue";

/**
 * Admin work queue. Compact rows grouped by status with creator / channel /
 * status filters. Each row's creator name links to the creator detail page
 * (no "Open admin tools" pill). A Directory tasks group lists missing
 * standard playbook steps across creators, lazily loaded when expanded.
 */

const STATUS_GROUPS = [
    { key: "todo", label: "To do", match: (s) => s === "needs_redo" || s === "draft_pending" },
    { key: "drafted", label: "Drafted", match: (s) => s === "draft_ready" },
    { key: "live", label: "Live", match: (s) => s === "live_verified" || s === "published" },
    { key: "live_not_indexed", label: "Live, not indexed", match: (s) => s === "live_not_indexed" },
];

const STATUS_LABELS = {
    draft_pending: "Draft pending",
    draft_ready: "Draft ready",
    needs_redo: "Needs redo",
    published: "Published",
    live_verified: "Live verified",
    live_not_indexed: "Live, not indexed",
};

export default function AdminWork() {
    const { request } = useApi();
    const [tasks, setTasks] = useState([]);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(true);
    // Filters and the open task live in the URL so a refresh keeps them.
    const [params, setParams] = useSearchParams();
    const setParam = (key) => (value) =>
        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                return next;
            },
            { replace: true },
        );
    const filterCreator = params.get("creator") || "";
    const filterChannel = params.get("channel") || "";
    const filterStatus = params.get("status") || "";
    const openTaskId = params.get("task") || null;
    const setFilterCreator = setParam("creator");
    const setFilterChannel = setParam("channel");
    const setFilterStatus = setParam("status");
    const setOpenTaskId = setParam("task");
    const [openGroup, setOpenGroup] = useState({
        todo: true,
        drafted: true,
        live: false,
        live_not_indexed: true,
        directory: false,
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request("/admin/placement/queue");
            setTasks(res?.tasks || []);
            setChannels(res?.channels || []);
        } catch (err) {
            toast.error(err?.message || "Could not load tasks.");
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        load();
    }, [load]);

    const creators = useMemo(() => {
        const seen = new Map();
        for (const t of tasks) {
            const c = t.creator;
            if (c?.id && !seen.has(c.id)) {
                seen.set(c.id, {
                    id: c.id,
                    label: `/@${c.samoony_handle || "no-handle"}`,
                });
            }
        }
        return Array.from(seen.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [tasks]);

    const filtered = useMemo(() => {
        return tasks.filter((t) => {
            if (filterCreator && t.creator?.id !== filterCreator) return false;
            if (filterChannel && t.channel_id !== filterChannel) return false;
            if (filterStatus && t.status !== filterStatus) return false;
            return true;
        });
    }, [tasks, filterCreator, filterChannel, filterStatus]);

    const grouped = useMemo(() => {
        const out = {};
        for (const g of STATUS_GROUPS) out[g.key] = [];
        for (const t of filtered) {
            const g = STATUS_GROUPS.find((x) => x.match(t.status));
            if (g) out[g.key].push(t);
        }
        return out;
    }, [filtered]);

    // A task opened from the URL may sit in a collapsed group; expand it.
    useEffect(() => {
        if (!openTaskId) return;
        const key = Object.keys(grouped).find((k) =>
            grouped[k].some((t) => t.id === openTaskId),
        );
        if (key) setOpenGroup((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }, [openTaskId, grouped]);

    return (
        <section data-testid="admin-work-root">
            <header className="mb-5">
                <h1 className="font-display text-3xl font-bold tracking-tight">
                    Work queue
                </h1>
                <p className="text-sm text-black/60 mt-1">
                    All placement tasks across creators, grouped by status.
                    Click a creator name to open their admin page.
                </p>
            </header>

            {/* Filters */}
            <div
                data-testid="admin-work-filters"
                className="bg-white border border-black/10 rounded-2xl p-3 mb-5 flex flex-wrap items-center gap-2"
            >
                <select
                    data-testid="admin-work-filter-creator"
                    value={filterCreator}
                    onChange={(e) => setFilterCreator(e.target.value)}
                    className="samoony-input !h-9"
                >
                    <option value="">Every creator</option>
                    {creators.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.label}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="admin-work-filter-channel"
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                    className="samoony-input !h-9"
                >
                    <option value="">Every channel</option>
                    {channels.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.label}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="admin-work-filter-status"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="samoony-input !h-9"
                >
                    <option value="">Every status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                            {v}
                        </option>
                    ))}
                </select>
                {(filterCreator || filterChannel || filterStatus) && (
                    <button
                        type="button"
                        data-testid="admin-work-filter-clear"
                        onClick={() => {
                            setFilterCreator("");
                            setFilterChannel("");
                            setFilterStatus("");
                        }}
                        className="text-xs text-black/50 hover:text-ink px-2"
                    >
                        Clear
                    </button>
                )}
            </div>

            {loading && <div className="text-sm text-black/50">Loading tasks...</div>}

            {!loading &&
                STATUS_GROUPS.map((g) => (
                    <Group
                        key={g.key}
                        label={g.label}
                        tasks={grouped[g.key] || []}
                        openTaskId={openTaskId}
                        setOpenTaskId={setOpenTaskId}
                        channels={channels}
                        onChanged={load}
                        isOpen={openGroup[g.key]}
                        onToggle={() =>
                            setOpenGroup((p) => ({ ...p, [g.key]: !p[g.key] }))
                        }
                        testidKey={g.key}
                    />
                ))}

            <DirectoryTasksGroup
                isOpen={openGroup.directory}
                onToggle={() =>
                    setOpenGroup((p) => ({ ...p, directory: !p.directory }))
                }
            />
        </section>
    );
}

function Group({
    label,
    tasks,
    openTaskId,
    setOpenTaskId,
    channels,
    onChanged,
    isOpen,
    onToggle,
    testidKey,
}) {
    return (
        <section
            data-testid={`admin-work-group-${testidKey}`}
            className="bg-white border border-black/10 rounded-2xl mb-4 overflow-hidden"
        >
            <button
                type="button"
                data-testid={`admin-work-group-toggle-${testidKey}`}
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
            >
                <div className="flex items-center gap-3">
                    <span className="samoony-label">{label}</span>
                    <span className="font-mono text-[11px] text-black/50">
                        {tasks.length}
                    </span>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-wider text-black/50">
                    {isOpen ? "Hide" : "Open"}
                </span>
            </button>
            {isOpen && (
                <ul className="divide-y divide-black/5 border-t border-black/5">
                    {tasks.length === 0 && (
                        <li className="px-4 py-3 text-sm text-black/50">
                            Nothing here.
                        </li>
                    )}
                    {tasks.map((t) => (
                        <li
                            key={t.id}
                            data-testid={`admin-work-row-${t.id}`}
                            className="px-4 py-3"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="text-sm">
                                        {t.creator?.id ? (
                                            <Link
                                                data-testid={`admin-work-row-creator-${t.id}`}
                                                to={`/admin/creators/${t.creator.id}`}
                                                className="underline underline-offset-4 decoration-black/20 hover:decoration-ink"
                                            >
                                                {t.creator?.name ||
                                                    `/@${t.creator?.samoony_handle}`}
                                            </Link>
                                        ) : (
                                            <span>
                                                {t.creator?.name || "—"}
                                            </span>
                                        )}{" "}
                                        <span className="font-mono text-[11px] text-black/50">
                                            /@{t.creator?.samoony_handle}
                                        </span>
                                    </div>
                                    <div className="font-mono text-[11px] text-black/60">
                                        {t.channel_label} ·{" "}
                                        {STATUS_LABELS[t.status] || t.status}
                                        {t.content?.title
                                            ? ` · ${t.content.title}`
                                            : ""}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {t.live_url && (
                                        <a
                                            href={t.live_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[11px] underline text-black/50 break-all"
                                        >
                                            Live
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`admin-work-row-open-${t.id}`}
                                        onClick={() =>
                                            setOpenTaskId(
                                                openTaskId === t.id ? null : t.id,
                                            )
                                        }
                                        className="samoony-chip"
                                    >
                                        {openTaskId === t.id ? "Close" : "Open"}
                                    </button>
                                </div>
                            </div>
                            {openTaskId === t.id && (
                                <div className="mt-3">
                                    <TaskDetail
                                        taskId={t.id}
                                        channels={channels}
                                        onChanged={onChanged}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

// Directory tasks group: lazy-loaded aggregate of missing standard playbook
// steps across creators. Expanding fires one /admin/creators list call plus
// one /admin/creators/:id/overview per approved, non-internal creator.
function DirectoryTasksGroup({ isOpen, onToggle }) {
    const { request } = useApi();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState(null); // null = not yet loaded

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await request("/admin/creators");
            const creators = (list?.creators || []).filter(
                (c) => c.approved_at && !c.is_internal && !c.archived_at,
            );
            const out = [];
            for (const c of creators) {
                try {
                    const ov = await request(
                        `/admin/creators/${c.id}/overview`,
                    );
                    const missing = (ov?.playbook || []).filter(
                        (s) =>
                            s.status === "todo" &&
                            !s.optional &&
                            s.key !== "samoony_page",
                    );
                    for (const m of missing) {
                        out.push({
                            creator_id: c.id,
                            samoony_handle: c.samoony_handle,
                            step_key: m.key,
                            step_label: m.label,
                            note: m.note,
                        });
                    }
                } catch {
                    // skip failing creator
                }
            }
            setRows(out);
        } catch (err) {
            toast.error(err?.message || "Could not load directory tasks.");
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        if (isOpen && rows === null && !loading) {
            void load();
        }
    }, [isOpen, rows, loading, load]);

    return (
        <section
            data-testid="admin-work-group-directory"
            className="bg-white border border-black/10 rounded-2xl mb-4 overflow-hidden"
        >
            <button
                type="button"
                data-testid="admin-work-group-toggle-directory"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
            >
                <div className="flex items-center gap-3">
                    <span className="samoony-label">Directory tasks</span>
                    <span className="font-mono text-[11px] text-black/50">
                        {rows ? rows.length : "..."}
                    </span>
                </div>
                <span className="font-mono text-[11px] uppercase tracking-wider text-black/50">
                    {isOpen ? "Hide" : "Open"}
                </span>
            </button>
            {isOpen && (
                <div className="border-t border-black/5">
                    {loading && (
                        <div className="px-4 py-3 text-sm text-black/50">
                            Loading directory tasks...
                        </div>
                    )}
                    {!loading && (rows?.length || 0) === 0 && (
                        <div
                            data-testid="admin-work-directory-empty"
                            className="px-4 py-3 text-sm text-black/60"
                        >
                            No standard directory steps outstanding.
                        </div>
                    )}
                    {!loading && (rows?.length || 0) > 0 && (
                        <ul className="divide-y divide-black/5">
                            {rows.map((r, i) => (
                                <li
                                    key={`${r.creator_id}:${r.step_key}:${i}`}
                                    data-testid={`admin-work-directory-row-${i}`}
                                    className="px-4 py-3 flex flex-wrap items-center justify-between gap-2"
                                >
                                    <div>
                                        <div className="text-sm">
                                            <Link
                                                to={`/admin/creators/${r.creator_id}`}
                                                className="underline underline-offset-4 decoration-black/20 hover:decoration-ink"
                                            >
                                                /@{r.samoony_handle}
                                            </Link>{" "}
                                            needs{" "}
                                            <strong className="font-medium">
                                                {r.step_label}
                                            </strong>
                                        </div>
                                        {r.note ? (
                                            <div className="font-mono text-[11px] text-black/50">
                                                {r.note}
                                            </div>
                                        ) : null}
                                    </div>
                                    <Link
                                        to={`/admin/creators/${r.creator_id}`}
                                        className="samoony-chip"
                                    >
                                        Open creator
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}
